import { useEffect, useState } from "react";
import { AxiosResponse } from "axios";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  IconButton,
  Modal,
  SxProps,
  // Typography,
} from "@mui/material";
import {
  ModalDrawerButtonStyles,
  ModalDrawerStyles,
  ModalHeader,
} from "../Common/styles/modal";
import { errorToastMessage, toastMessage } from "../../utils/toast";
import http from "../../utils/http";
import { useParams } from "react-router-dom";
import { ExpandMore, ExpandLess } from "@mui/icons-material";
import { useAppSelector } from "../../Redux/hooks";
import { submitFormat } from "../../utils/export";
import { featuresMap } from "../../utils/features";
import { DrawerForm } from "../Common/styles/drawer";

const CheckboxWrapper: SxProps = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  height: "56px",
};
const CheckBoxLabelStyle: SxProps = {
  fontWeight: 500,
  color: "text.primary",
};
type Props = {
  showModal: boolean;
  closeModal: () => void;
  selectedParticipantIds?: string[];
};

const ExportAllModal = ({
  showModal,
  closeModal,
  selectedParticipantIds,
}: Props) => {
  const { id: studyId } = useParams();
  const features = useAppSelector((state) => state.study.features);
  const [submitLoader, setSubmitLoader] = useState(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [checked, setChecked] = useState<any>({});
  // const [includeQueries, setInlcudeQueries] = useState<boolean>(false);
  // const [includeComments, setInlcudeComments] = useState<boolean>(false);

  const submitHandler = async () => {
    try {
      setSubmitLoader(true);
      let selectedIds = submitFormat(checked);
      const body = {
        selectedForms: {
          screeningFormsIds: selectedIds.screening,
          visitFormsIds: selectedIds.visits,
          repeatedDataFormsIds: selectedIds.repeating_data,
          surveyFormsIds: selectedIds.surveys,
          screeningPhaseIds: selectedIds.screeningPhaseIds,
          visitPhaseIds: selectedIds.visitsPhaseIds,
          repeatedDataPhaseIds: selectedIds.repeatingDataPhaseIds,
          surveyPhaseIds: selectedIds.surveysPhaseIds,
        },
        exportData: "all",
        ...(selectedParticipantIds?.length
          ? { participantIds: selectedParticipantIds }
          : {}),
      };
      let res: AxiosResponse;
      res = await http.post(`/study/${studyId}/exports`, body);
      toastMessage("success", res?.data?.message);
      closeModal();
      setSubmitLoader(false);
    } catch (err) {
      setSubmitLoader(false);
      errorToastMessage(err as Error);
    }
  };

  useEffect(() => {
    const fetchPhaseData = async () => {
      try {
        setLoading(true);
        let formattedData1: any[] = [];
        let formattedData2: any[] = [];
        let formattedData3: any[] = [];
        let formattedData4: any[] = [];

        if (features?.[featuresMap.visit]) {
          const res1: AxiosResponse = await http.get(
            `/study/${studyId}/study-phase?category=visit`
          );
          formattedData1 = res1.data?.data
            ?.map((item: any) => ({
              id: item?.id,
              category: item?.category,
              name: item?.name || "",
              selected: "no",
              expanded: false,
              forms: item?.phaseForms?.map((item: any) => ({
                id: item?.form?.id,
                name: item?.form?.name,
                selected: false,
                expanded: false,
              })),
            }))
            .filter((item: any) => item?.forms?.length > 0);

          const res4: AxiosResponse = await http.get(
            `/study/${studyId}/study-phase?category=screening`
          );
          formattedData4 = res4.data?.data
            ?.map((item: any) => ({
              id: item?.id,
              category: item?.category,
              name: item?.name || "",
              selected: "no",
              expanded: false,
              forms: item?.phaseForms?.map((item: any) => ({
                id: item?.form?.id,
                name: item?.form?.name,
                selected: false,
                expanded: false,
              })),
            }))
            .filter((item: any) => item?.forms?.length > 0);
        }
        if (features?.[featuresMap.repeatedData]) {
          const res2: AxiosResponse = await http.get(
            `/study/${studyId}/study-phase?category=repeated_data`
          );

          formattedData2 = res2.data?.data
            ?.map((item: any) => ({
              id: item?.id,
              category: item?.category,
              name: item?.name || "",
              selected: "no",
              expanded: false,
              forms: item?.phaseForms?.map((item: any) => ({
                id: item?.form?.id,
                name: item?.form?.name,
                selected: false,
                expanded: false,
              })),
            }))
            .filter((item: any) => item?.forms?.length > 0);
        }
        if (features?.[featuresMap.survey]) {
          const res3: AxiosResponse = await http.get(
            `/study/${studyId}/study-phase?category=survey`
          );
          formattedData3 = res3.data?.data
            ?.map((item: any) => ({
              id: item?.id,
              category: item?.category,
              name: item?.name || "",
              selected: "no",
              expanded: false,
              forms: item?.phaseForms?.map((item: any) => ({
                id: item?.form?.id,
                name: item?.form?.name,
                selected: false,
                expanded: false,
              })),
            }))
            .filter((item: any) => item?.forms?.length > 0);
        }

        const dataArray = [];
        if (formattedData4.length > 0) {
          dataArray.push({
            id: "screening",
            name: "Screening",
            selected: "no",
            expanded: false,
            formattedData: formattedData4,
          });
        }
        if (formattedData1.length > 0) {
          dataArray.push({
            id: "visit",
            name: "Visit",
            selected: "no",
            expanded: false,
            formattedData: formattedData1,
          });
        }
        if (formattedData2.length > 0) {
          dataArray.push({
            id: "repeating",
            name: "Repeating Data",
            selected: "no",
            expanded: false,
            formattedData: formattedData2,
          });
        }
        if (formattedData3.length > 0) {
          dataArray.push({
            id: "survey",
            name: "Surveys",
            selected: "no",
            expanded: false,
            formattedData: formattedData3,
          });
        }

        const data = {
          id: "entire_study",
          name: "Export Entire Structure",
          selected: "no",
          expanded: false,
          data: dataArray,
        };

        setChecked(data);
        setLoading(false);
      } catch (err) {
        setLoading(false);
        errorToastMessage(err as Error);
      }
    };
    fetchPhaseData();
  }, [studyId, features]);

  const handleExpand = (key: string, type: string) => {
    const updatedChecked = {
      ...checked,
      expanded: key === "entire_study" ? !checked.expanded : checked.expanded,
      data: checked.data.map((category: any) => {
        if (category.id === type) {
          if (key === type) {
            return {
              ...category,
              expanded: !category.expanded,
            };
          } else {
            const updatedData = category.formattedData.map((item: any) => {
              if (item.id === key) {
                return {
                  ...item,
                  expanded: !item.expanded,
                };
              }
              return item;
            });
            return {
              ...category,
              formattedData: updatedData,
            };
          }
        }
        return category;
      }),
    };

    setChecked(updatedChecked);
  };

  const handleChange = (
    id: string,
    event: React.ChangeEvent<HTMLInputElement>,
    type: string
  ) => {
    let updatedChecked;

    if (type === "entire_study") {
      const updatedData = checked?.data?.map((category: any) => ({
        ...category,
        selected: event.target.checked ? "yes" : "no",
        formattedData: category?.formattedData?.map((phase: any) => ({
          ...phase,
          selected: event.target.checked ? "yes" : "no",
          forms: phase?.forms?.map((form: any) => ({
            ...form,
            selected: event.target.checked,
          })),
        })),
      }));
      updatedChecked = {
        ...checked,
        selected: event.target.checked ? "yes" : "no",
        data: updatedData,
      };
    } else {
      const updatedData = checked?.data?.map((category: any) => {
        let updatedFormattedData;
        let categorySelected = category.selected;

        if (type === category.id) {
          updatedFormattedData = category?.formattedData?.map((phase: any) => ({
            ...phase,
            selected: event.target.checked ? "yes" : "no",
            forms: phase?.forms?.map((form: any) => ({
              ...form,
              selected: event.target.checked,
            })),
          }));
          categorySelected = event.target.checked ? "yes" : "no";
        } else if (type === "phases") {
          updatedFormattedData = category?.formattedData?.map((phase: any) => {
            if (phase.id === id) {
              const updatedForms = phase?.forms?.map((form: any) => ({
                ...form,
                selected: event.target.checked,
              }));
              return {
                ...phase,
                selected: event.target.checked ? "yes" : "no",
                forms: updatedForms,
              };
            }
            return phase;
          });
        } else if (type === "forms") {
          updatedFormattedData = category?.formattedData?.map((phase: any) => {
            const updatedForms = phase?.forms?.map((form: any) => {
              if (form.id === id) {
                return {
                  ...form,
                  selected: event.target.checked,
                };
              }
              return form;
            });
            const allSelected =
              updatedForms.length > 0 &&
              updatedForms.every((form: any) => form.selected);
            const anySelected =
              updatedForms.length > 0 &&
              updatedForms.some((form: any) => form.selected);
            const phaseSelected = allSelected
              ? "yes"
              : anySelected
              ? "partial"
              : "no";
            return {
              ...phase,
              selected: phaseSelected,
              forms: updatedForms,
            };
          });
        }

        if (updatedFormattedData) {
          const allPhasesSelected =
            updatedFormattedData.length > 0 &&
            updatedFormattedData.every(
              (phase: any) => phase.selected === "yes"
            );
          const anyPhasesSelected =
            updatedFormattedData.length > 0 &&
            updatedFormattedData.some(
              (phase: any) =>
                phase.selected === "yes" || phase.selected === "partial"
            );
          categorySelected = allPhasesSelected
            ? "yes"
            : anyPhasesSelected
            ? "partial"
            : "no";
        }

        return updatedFormattedData
          ? {
              ...category,
              selected: categorySelected,
              formattedData: updatedFormattedData,
            }
          : category;
      });

      const allCategoriesSelected =
        updatedData.length > 0 &&
        updatedData.every((category: any) => category.selected === "yes");
      const anyCategoriesSelected =
        updatedData.length > 0 &&
        updatedData.some(
          (category: any) =>
            category.selected === "yes" || category.selected === "partial"
        );

      updatedChecked = {
        ...checked,
        selected: allCategoriesSelected
          ? "yes"
          : anyCategoriesSelected
          ? "partial"
          : "no",
        data: updatedData,
      };
    }

    setChecked(updatedChecked);
  };

  const SubChildren = ({ data }: any) => {
    return (
      <>
        {data?.map((item: any, index: number) => (
          <Box key={item?.id}>
            <Box
              key={item?.id}
              sx={{
                ...CheckboxWrapper,
                ml: 9,
              }}
            >
              <FormControlLabel
                componentsProps={{
                  typography: {
                    sx: {
                      ...CheckBoxLabelStyle,
                      fontSize: 14,
                      fontWeight: 400,
                    },
                  },
                }}
                label={item?.name}
                control={
                  <Checkbox
                    checked={item?.selected}
                    indeterminate={item?.selected === "partial"}
                    onChange={(e) => handleChange(item?.id, e, "forms")}
                  />
                }
              />
            </Box>
            <Divider />
          </Box>
        ))}
      </>
    );
  };

  return (
    <Modal open={showModal} onClose={closeModal}>
      <Box sx={ModalDrawerStyles}>
        <ModalHeader title={"Export"} onCloseClick={closeModal} />
        {!loading ? (
          <Box sx={DrawerForm}>
            {/* <Box sx={{ display: "flex", gap: 2 }}>
              <FormControlLabel
                label={
                  <Typography
                    fontSize={16}
                    fontWeight={500}
                    color="text.primary"
                  >
                    Include Queries
                  </Typography>
                }
                control={
                  <Checkbox
                    checked={includeQueries}
                    onChange={(event) => {
                      setInlcudeQueries(event.target.checked);
                    }}
                  />
                }
              />
              <FormControlLabel
                label={
                  <Typography
                    fontSize={16}
                    fontWeight={500}
                    color="text.primary"
                  >
                    Include Comments
                  </Typography>
                }
                control={
                  <Checkbox
                    checked={includeComments}
                    onChange={(event) => {
                      setInlcudeComments(event.target.checked);
                    }}
                  />
                }
              />
            </Box> */}
            <Box>
              <Box
                sx={{ ...CheckboxWrapper, borderBottom: "1px solid #e0e3eb" }}
              >
                <FormControlLabel
                  label={checked.name}
                  componentsProps={{
                    typography: {
                      sx: { ...CheckBoxLabelStyle, fontSize: 18 },
                    },
                  }}
                  control={
                    <Checkbox
                      checked={checked.selected === "yes"}
                      indeterminate={checked.selected === "partial"}
                      onChange={(e) =>
                        handleChange(checked.id, e, "entire_study")
                      }
                    />
                  }
                />
                {/* <IconButton onClick={() => handleExpand(checked.id, checked.id)}>
                {checked.expanded ? <ExpandMore /> : <ExpandLess />}
              </IconButton> */}
              </Box>

              <>
                {checked?.data?.map((category: any) => (
                  <Box key={category.id}>
                    <Box>
                      <Box
                        sx={{
                          ...CheckboxWrapper,
                          ml: 3,
                        }}
                      >
                        <FormControlLabel
                          label={category.name}
                          componentsProps={{
                            typography: {
                              sx: { ...CheckBoxLabelStyle, fontSize: 16 },
                            },
                          }}
                          control={
                            <Checkbox
                              checked={category.selected === "yes"}
                              indeterminate={category.selected === "partial"}
                              onChange={(e) =>
                                handleChange(category.id, e, category.id)
                              }
                            />
                          }
                        />
                        <IconButton
                          onClick={() => handleExpand(category.id, category.id)}
                        >
                          {category.expanded ? <ExpandLess /> : <ExpandMore />}
                        </IconButton>
                      </Box>
                      <Divider />
                    </Box>

                    {category?.expanded && (
                      <>
                        {category?.formattedData?.map((item: any) => (
                          <Box key={item?.id}>
                            <Box>
                              <Box
                                sx={{
                                  ...CheckboxWrapper,
                                  ml: 6,
                                }}
                              >
                                <FormControlLabel
                                  label={item?.name}
                                  componentsProps={{
                                    typography: {
                                      sx: {
                                        ...CheckBoxLabelStyle,
                                        fontSize: 14,
                                      },
                                    },
                                  }}
                                  control={
                                    <Checkbox
                                      checked={item?.selected === "yes"}
                                      indeterminate={
                                        item?.selected === "partial"
                                      }
                                      onChange={(e) =>
                                        handleChange(item?.id, e, "phases")
                                      }
                                    />
                                  }
                                />
                                <IconButton
                                  onClick={() =>
                                    handleExpand(item.id, category.id)
                                  }
                                >
                                  {item?.expanded ? (
                                    <ExpandLess />
                                  ) : (
                                    <ExpandMore />
                                  )}
                                </IconButton>
                              </Box>
                              <Divider />
                            </Box>
                            {item?.expanded && (
                              <SubChildren data={item?.forms} />
                            )}
                          </Box>
                        ))}
                      </>
                    )}
                  </Box>
                ))}
              </>
            </Box>
            <Box sx={{ mt: "auto", pt: 2 }}>
              <Divider />
              <Box sx={ModalDrawerButtonStyles}>
                {!submitLoader ? (
                  <>
                    <Button onClick={closeModal} variant="outlined">
                      Cancel
                    </Button>
                    <Button onClick={submitHandler} variant="contained">
                      Export Data
                    </Button>
                  </>
                ) : (
                  <CircularProgress size={25} />
                )}
              </Box>
            </Box>
          </Box>
        ) : (
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <CircularProgress size={25} />
          </Box>
        )}
      </Box>
    </Modal>
  );
};

export default ExportAllModal;
